<template>
  <div class="ai-frame">
    <div class="ai-splash" :class="{ 'is-fading': isFading }" v-if="showSplash">
      <div class="ai-splash__logo">
        <img src="../assets/swift_logo_ai.png" alt="swiftAI" />
        <div class="ai-splash__bar"><div class="ai-splash__fill"></div></div>
      </div>
    </div>

    <iframe ref="gsaiIframe"
            sandbox="allow-scripts allow-popups allow-modals allow-forms allow-downloads allow-same-origin allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation"
            :src="url"
            class="ai-frame__iframe"
            v-show="!showSplash"></iframe>
  </div>
</template>

<script>
  // Ported from SwiftProcessingSPA's components/ai.vue. Only deviation: that copy shipped an
  // UNSCOPED <style> with `* { margin:0; padding:0 }` and `body,html { font-family: Arial }`,
  // which would have flattened this app's global typography. Styles here are scoped and the
  // resets are dropped; everything the iframe needs lives inside the iframe anyway.
  export default {
    name: 'AiFrame',
    props: {
      url: String,
      azureId: String,
      userName: String
    },
    data() {
      return {
        showSplash: true,
        isFading: false
      };
    },
    async mounted() {
      const iframeEl = this.$refs.gsaiIframe;
      if (iframeEl) {
        // onload, not mounted: the AI site has to be listening before the handshake lands.
        iframeEl.onload = async () => {
          let azureId = this.azureId || localStorage.getItem('azure_id');
          let userName = this.userName || localStorage.getItem('user_name') || '';
          let maxRetries = 10;

          while ((!azureId || !userName) && maxRetries > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
            azureId = this.azureId || localStorage.getItem('azure_id');
            userName = this.userName || localStorage.getItem('user_name') || '';
            maxRetries--;
          }

          if (!azureId) {
            console.error('[SWIFT SPA] Failed to get azure_id after 10 retries');
            return;
          }
          if (!userName) {
            console.warn('[SWIFT SPA] userName not available after 10 retries, proceeding anyway');
          }

          await new Promise(resolve => setTimeout(resolve, 3000));

          let targetOrigin;
          try {
            targetOrigin = new URL(this.url).origin;
          } catch (error) {
            console.error('[SWIFT SPA] Invalid URL provided for iframe:', this.url, error);
            return;
          }

          // The AI site sets its listener up late and gives no ack, so the send is repeated.
          for (let i = 0; i < 20; i++) {
            if (iframeEl.contentWindow) {
              try {
                iframeEl.contentWindow.postMessage(
                  { type: 'user_data', azureId, userName, parentURL: window.location.origin },
                  targetOrigin
                );
              } catch (error) {
                console.warn(`[SWIFT SPA] Failed to send user data on attempt ${i + 1}:`, error);
              }
            }
            if (i < 19) await new Promise(resolve => setTimeout(resolve, 500));
          }
        };
      }

      this.splashFade = setTimeout(() => {
        this.isFading = true;
        this.splashHide = setTimeout(() => { this.showSplash = false; }, 1000);
      }, 2500);
    },
    beforeUnmount() {
      clearTimeout(this.splashFade);
      clearTimeout(this.splashHide);
    }
  };
</script>

<style scoped>
  .ai-frame {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
    background: #fff;
  }

  /* Absolute, not just width/height 100%: an iframe with no resolvable box falls back to
     its 300x150 intrinsic size instead of collapsing, so the failure is silent and ugly. */
  .ai-frame__iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }

  .ai-splash {
    position: absolute;
    inset: 0;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
    transition: opacity 1s ease;
  }

  .ai-splash.is-fading {
    opacity: 0;
    pointer-events: none;
  }

  .ai-splash__logo {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .ai-splash__logo img {
    width: 260px;
    max-width: 70%;
    height: auto;
    margin-bottom: 30px;
  }

  .ai-splash__bar {
    width: 220px;
    max-width: 60%;
    height: 6px;
    background: #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 0 5px #ffd85c88;
  }

  .ai-splash__fill {
    height: 100%;
    width: 0;
    background: linear-gradient(90deg, #f9c744, #ffdd80, #f9c744);
    border-radius: 4px;
    animation: ai-splash-load 2s ease-in-out forwards;
  }

  @keyframes ai-splash-load {
    0%   { width: 0; }
    100% { width: 100%; }
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-splash__fill { animation-duration: .01s; }
    .ai-splash { transition: none; }
  }
</style>
